<template>
  <div class="operator-base-permission-group">
    <div class="permission-group-title">
      {{ title }}
    </div>
    <div class="permission-group-controls">
      <template v-if="!isEditing">
        <div class="list">
          <v-chip
            v-for="permission in visiblePermissionKeys"
            :key="permission"
            color="primary"
            :close="!readonly"
            outlined
            @click:close="$emit('update-group', { [permission]: false })"
          >
            {{ enabledPermissions[permission] }}
          </v-chip>
          <v-chip
            v-if="totalNumberOfPermissions > MAX_DISPLAYED_PERMISSIONS"
            outlined
            color="primary"
          >
            +{{ totalNumberOfPermissions - MAX_DISPLAYED_PERMISSIONS }} more
          </v-chip>
        </div>
        <div class="add">
          <v-icon
            :disabled="readonly"
            @click="isEditing = true"
          >
            add
          </v-icon>
        </div>
      </template>
      <v-select
        v-else
        :value="Object.keys(enabledPermissions)"
        :items="permissionOptions"
        item-value="key"
        multiple
        dense
        hide-details
        class="permission-select"
        @input="localModelValue = $event"
        @blur="onBlur"
      >
        <template #item="{ item }">
          {{ item.name }}
        </template>

        <template #selection="{ item, index }">
          <v-chip
            color="primary"
            outlined
            v-if="index < MAX_DISPLAYED_PERMISSIONS"
          >
            {{ item.name }}
          </v-chip>

          <v-chip
            v-if="index === MAX_DISPLAYED_PERMISSIONS"
            outlined
            color="primary"
            class="more-chip"
          >
            +{{ totalNumberOfPermissions - MAX_DISPLAYED_PERMISSIONS }} more
          </v-chip>
        </template>

        <v-icon slot="append" @click="isEditing = false">
          close
        </v-icon>
      </v-select>
    </div>
  </div>
</template>

<script>
import { difference } from 'lodash';
const MAX_DISPLAYED_PERMISSIONS = 5;

export default {
  props: {
    title: {
      type: String,
      default: ''
    },
    enabledPermissions: {
      type: Object,
      default: () => ({})
    },
    permissionOptions: {
      type: Array,
      default: () => ([])
    },
    readonly: Boolean,
  },
  data() {
    return {
      MAX_DISPLAYED_PERMISSIONS,
      isEditing: false,
      localModelValue: Object.keys(this.enabledPermissions)
    }
  },
  emits: ['update-group'],
  computed: {
    totalNumberOfPermissions() {
      return Object.values(this.enabledPermissions).length;
    },
    visiblePermissionKeys() {
      return Object.keys(this.enabledPermissions).slice(0, MAX_DISPLAYED_PERMISSIONS);
    }
  },
  methods: {
    onBlur() {
      const enabledPermissionKeys = Object.keys(this.enabledPermissions);
      const arraysAreIdentical = this.localModelValue.length === enabledPermissionKeys.length
            && enabledPermissionKeys.every(key => this.localModelValue.includes(key));
      if (arraysAreIdentical) {
        this.isEditing = false;
        return;
      }

      const addedKeys = difference(this.localModelValue, enabledPermissionKeys);
      const removedKeys = difference(enabledPermissionKeys, this.localModelValue);

      const diff = {
        ...Object.fromEntries(addedKeys.map(key => [key, true])),
        ...Object.fromEntries(removedKeys.map(key => [key, false])),
      };

      this.$emit('update-group', diff);
    }
  },
  watch: {
    readonly(newValue, oldValue) {
      if (!newValue && oldValue) {
        this.isEditing = false;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.operator-base-permission-group {
  --border-color: #E0E0E0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border: 1px solid var(--border-color);
  border-bottom: none;

  &:last-child {
    border-bottom: 1px solid var(--border-color);
  }
  .permission-group-title {
    color: #000;
    font-weight: 600;
  }

  .permission-group-controls {
    display: flex;
    align-items: center;
    gap: 1rem;

    .permission-select {
      :before {
        margin-bottom: -5px;
      }

      .more-chip {
        margin-left: auto;
        order: 2;
      }
    }

    .list {
      .v-chip {
        &:not(:last-child) {
          margin-right: 0.5rem;
        }
      }
    }

    .add {
      .v-icon {
        background-color: #4caf50;
        border-radius: 50%;
        color: #fff;
      }
    }

    .v-select {
      margin-top: 0;
    }
  }
}
</style>
